import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useId, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {Text, View} from "@khanacademy/wonder-blocks-core";
import {ModalDialog, ModalPanel} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingMedium} from "@khanacademy/wonder-blocks-typography";
import {ErrorMessageWrapper} from "@repo/component-error-message";
import {useMutation} from "@repo/data-gql";
import {mediaQueries} from "@repo/shared-styles";

import LoadingSpinner from "#districts-account-link-package/components/loading-spinner.tsx";

import {CANCEL_STRIPE_SUBSCRIPTION_MUTATION} from "./graphql.ts";

type Props = {
    // Using onNo instead of something like onCancel to avoid confusion with
    // this being a cancel subscription modal.
    onNo: () => void;
    onYes: () => void;
    // Once we implement switching to teacher tools, we'll leverage this to
    // show the correct message to the user.
    isTeacher?: boolean;
    // subscription to cancel
    subscriptionId: string;
};

export default function ConfirmSubscriptionCancel(
    props: Props,
): React.ReactElement {
    const [error, setError] = useState("");
    const [cancelStripeSubscription, cancelResults] = useMutation(
        CANCEL_STRIPE_SUBSCRIPTION_MUTATION,
    );
    // The ID used by Screen Readers to identify the dialog title.
    const dialogTitleId = useId();

    const loading = isLoading([cancelResults]);
    const hasError = error;

    const {subscriptionId} = props;

    return (
        <ModalDialog
            aria-labelledby={dialogTitleId}
            style={[styles.modal, hasError ? styles.modalWithError : undefined]}
        >
            <ModalPanel
                closeButtonVisible={true}
                content={
                    <View>
                        <HeadingMedium
                            id={dialogTitleId}
                            style={styles.heading}
                        >
                            {t`Are you sure you want to cancel your Khanmigo subscription?`}
                        </HeadingMedium>
                        <Text style={styles.text}>
                            {t`This will cancel your subscription at the end of the billing period.`}
                        </Text>
                        {loading ? (
                            <LoadingSpinner />
                        ) : (
                            hasError && (
                                <ErrorMessageWrapper>
                                    {t`An error occurred, please try again or contact support.`}
                                </ErrorMessageWrapper>
                            )
                        )}
                    </View>
                }
                footer={
                    <>
                        <Button
                            kind="secondary"
                            onClick={() => {
                                props.onNo();
                            }}
                        >
                            {t`No`}
                        </Button>
                        <Button
                            kind="primary"
                            disabled={loading}
                            color="destructive"
                            onClick={async () => {
                                const mutationResult =
                                    await cancelStripeSubscription({
                                        subscriptionId,
                                    });
                                if (
                                    mutationResult.status === "error" ||
                                    // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'Result<{ cancelStripeSubscription: CancelStripeSubscription_cancelStripeSubscription | null | undefined; }>'.
                                    mutationResult.data
                                        ?.cancelStripeSubscription.error
                                ) {
                                    setError(
                                        t`An error occurred, please try again or contact support.`,
                                    );

                                    return;
                                }

                                props.onYes();
                            }}
                            style={styles.primaryButton}
                        >
                            {t`Yes, cancel subscription`}
                        </Button>
                    </>
                }
            />
        </ModalDialog>
    );
}

function isLoading(gqlResults: Array<any>): boolean {
    return gqlResults.some((result) => result?.status === "loading");
}

const styles = StyleSheet.create({
    modal: {
        width: 500,
        height: 450,
        maxWidth: "90%",
        maxHeight: "90%",
        [mediaQueries.mdOrLarger]: {
            height: 400,
        },
    },
    modalWithError: {
        height: 550,
        [mediaQueries.mdOrLarger]: {
            height: 450,
        },
    },
    heading: {
        marginTop: spacing.xSmall_8,
        marginBottom: spacing.xLarge_32,
    },
    text: {
        marginBottom: spacing.medium_16,
    },
    primaryButton: {
        marginLeft: spacing.medium_16,
    },
});
